import React, { useState, useEffect } from "react";
import { db } from "../firebase";
import { collection, addDoc, getDocs, updateDoc, doc, query, where, Timestamp } from "firebase/firestore";

// 管理者が打刻を直すための画面。
// 記録を消さずに残すため、削除は「取り消し」として印を付けるだけにしてある（cancelled）。
// 時刻を直した場合も、元の時刻を originalTimestamp に残す。
// 給与計算はこの結果を読むので、直したあとに再計算すれば金額に反映される。

const pad = (n) => String(n).padStart(2, "0");
const ymOf = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
const dateKey = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const toLocalInput = (d) => `${dateKey(d)}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
const DOW = ["日", "月", "火", "水", "木", "金", "土"];

function AttendanceEdit({ user, companyId }) {
  const [employees, setEmployees] = useState([]);
  const [uid, setUid]         = useState("");
  const [yearMonth, setYM]    = useState(ymOf(new Date()));
  const [punches, setPunches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy]       = useState(false);
  const [error, setError]     = useState("");
  const [message, setMessage] = useState("");
  const [editing, setEditing] = useState(null);   // { punch } 時刻の修正
  const [adding, setAdding]   = useState(null);   // { date } 打刻の追加

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDocs(query(
          collection(db, "users"),
          where("companyId", "==", companyId),
          where("status", "==", "approved")
        ));
        const list = snap.docs.map(d => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (a.nameKana || a.name || "").localeCompare(b.nameKana || b.name || "", "ja"));
        setEmployees(list);
      } catch (e) {
        console.error("社員一覧の取得に失敗しました:", e);
        setError("社員一覧を取得できませんでした。");
      }
    })();
  }, [companyId]);

  useEffect(() => { if (uid) loadPunches(); }, [uid, yearMonth]);

  const loadPunches = async () => {
    setLoading(true); setError(""); setMessage("");
    try {
      const [y, m] = yearMonth.split("-").map(Number);
      const from = new Date(y, m - 1, 1, 0, 0, 0);
      const to   = new Date(y, m, 0, 23, 59, 59);
      // その月だけを読む。全期間を取ると年々読み取り量が増えるため。
      const snap = await getDocs(query(
        collection(db, "attendance"),
        where("uid", "==", uid),
        where("timestamp", ">=", Timestamp.fromDate(from)),
        where("timestamp", "<=", Timestamp.fromDate(to))
      ));
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }))
        .filter(a => a.timestamp?.toDate)
        .map(a => ({ ...a, at: a.timestamp.toDate() }))
        .sort((a, b) => a.at - b.at);
      setPunches(list);
    } catch (e) {
      console.error("勤怠の取得に失敗しました:", e);
      setError("勤怠を取得できませんでした。権限をご確認ください。");
    } finally {
      setLoading(false);
    }
  };

  // 日ごとにまとめ、打刻漏れを見つける
  const byDay = () => {
    const map = {};
    punches.filter(p => !p.cancelled).forEach(p => {
      const k = dateKey(p.at);
      (map[k] = map[k] || []).push(p);
    });
    // 取り消し済みも、その日に何があったか分かるよう別に持つ
    const cancelled = {};
    punches.filter(p => p.cancelled).forEach(p => {
      const k = dateKey(p.at);
      (cancelled[k] = cancelled[k] || []).push(p);
    });
    const keys = [...new Set([...Object.keys(map), ...Object.keys(cancelled)])].sort();
    return keys.map(k => {
      const list = map[k] || [];
      const ins  = list.filter(p => p.type === "出勤");
      const outs = list.filter(p => p.type === "退勤");
      let issue = "";
      if (list.length > 0) {
        if (ins.length === 0)       issue = "出勤の打刻がありません";
        else if (outs.length === 0) issue = "退勤の打刻がありません";
        else if (outs[outs.length - 1].at <= ins[0].at) issue = "退勤が出勤より前になっています";
      }
      return { date: k, list, cancelled: cancelled[k] || [], issue };
    });
  };

  const saveEdit = async (punch, newAt, reason) => {
    setBusy(true); setError("");
    try {
      await updateDoc(doc(db, "attendance", punch.id), {
        timestamp: Timestamp.fromDate(newAt),
        // 元の時刻は上書きせずに残す（最初の修正のときだけ記録する）
        ...(punch.originalTimestamp ? {} : { originalTimestamp: punch.timestamp }),
        editedAt: Timestamp.now(),
        editedBy: user.email,
        editReason: reason,
      });
      setEditing(null);
      await loadPunches();
      setMessage("打刻を修正しました。給与計算をやり直すと金額に反映されます。");
    } catch (e) {
      console.error("打刻の修正に失敗しました:", e);
      setError("修正に失敗しました。権限をご確認ください。");
    } finally { setBusy(false); }
  };

  const cancelPunch = async (punch, reason) => {
    setBusy(true); setError("");
    try {
      await updateDoc(doc(db, "attendance", punch.id), {
        cancelled: true,
        cancelledAt: Timestamp.now(),
        cancelledBy: user.email,
        cancelReason: reason,
      });
      await loadPunches();
      setMessage("打刻を取り消しました。記録は残っています。");
    } catch (e) {
      console.error("打刻の取り消しに失敗しました:", e);
      setError("取り消しに失敗しました。");
    } finally { setBusy(false); }
  };

  const restorePunch = async (punch) => {
    setBusy(true); setError("");
    try {
      await updateDoc(doc(db, "attendance", punch.id), {
        cancelled: false,
        restoredAt: Timestamp.now(),
        restoredBy: user.email,
      });
      await loadPunches();
      setMessage("取り消しを元に戻しました。");
    } catch (e) {
      console.error("復元に失敗しました:", e);
      setError("元に戻せませんでした。");
    } finally { setBusy(false); }
  };

  const addPunch = async (type, at, reason) => {
    setBusy(true); setError("");
    try {
      const emp = employees.find(e => e.id === uid);
      await addDoc(collection(db, "attendance"), {
        uid,
        email: emp?.email || "",
        name: emp?.name || "",
        companyId,
        type,
        timestamp: Timestamp.fromDate(at),
        source: "manual",
        addedBy: user.email,
        addedAt: Timestamp.now(),
        addReason: reason,
      });
      setAdding(null);
      await loadPunches();
      setMessage("打刻を追加しました。給与計算をやり直すと金額に反映されます。");
    } catch (e) {
      console.error("打刻の追加に失敗しました:", e);
      setError("追加に失敗しました。権限をご確認ください。");
    } finally { setBusy(false); }
  };

  const days = uid ? byDay() : [];
  const issues = days.filter(d => d.issue);
  const hm = (d) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;

  return (
    <div>
      <p style={st.lead}>
        打刻の押し忘れや時刻の誤りを直せます。取り消した打刻も記録として残り、いつ誰がなぜ直したかが分かるようにしています。
      </p>

      <div style={st.picker}>
        <div>
          <label style={st.label}>社員</label>
          <select style={st.select} value={uid} onChange={e => setUid(e.target.value)}>
            <option value="">選択してください</option>
            {employees.map(e => (
              <option key={e.id} value={e.id}>{e.name || e.email}{e.memberNo ? `（${e.memberNo}）` : ""}</option>
            ))}
          </select>
        </div>
        <div>
          <label style={st.label}>対象年月</label>
          <input style={st.month} type="month" value={yearMonth} onChange={e => setYM(e.target.value)} />
        </div>
        {uid && (
          <button style={st.addBtn} onClick={() => setAdding({ date: `${yearMonth}-01` })}>＋ 打刻を追加</button>
        )}
      </div>

      {error   && <p style={st.error}>{error}</p>}
      {message && <p style={st.ok}>{message}</p>}

      {!uid ? <p style={st.empty}>社員を選択してください</p>
        : loading ? <p style={st.empty}>読み込み中...</p>
          : days.length === 0 ? <p style={st.empty}>この月の打刻はありません</p>
            : (
              <>
                {issues.length > 0 && (
                  <div style={st.warn}>
                    ⚠️ 打刻が揃っていない日が {issues.length}日あります。この状態では労働時間に加算されません。
                  </div>
                )}
                {days.map(d => {
                  const dow = new Date(d.date + "T00:00:00").getDay();
                  return (
                    <div key={d.date} style={{ ...st.dayCard, ...(d.issue ? st.dayBad : {}) }}>
                      <div style={st.dayHead}>
                        <strong>{d.date}（{DOW[dow]}）</strong>
                        {d.issue && <span style={st.issue}>{d.issue}</span>}
                        <button style={st.smallAdd} onClick={() => setAdding({ date: d.date })}>＋ 追加</button>
                      </div>

                      {d.list.map(p => (
                        <div key={p.id} style={st.punchRow}>
                          <span style={{ ...st.type, color: p.type === "出勤" ? "#27ae60" : "#e74c3c" }}>{p.type}</span>
                          <span style={st.time}>{hm(p.at)}</span>
                          {p.originalTimestamp && (
                            <span style={st.tag}>修正済み（元 {hm(p.originalTimestamp.toDate())}）</span>
                          )}
                          {p.source === "manual" && <span style={st.tag}>手動追加</span>}
                          {p.source === "kiosk"  && <span style={st.tagPlain}>スマホ</span>}
                          <span style={{ flex: 1 }} />
                          <button style={st.miniBtn} disabled={busy} onClick={() => setEditing({ punch: p })}>時刻を直す</button>
                          <button style={st.miniDel} disabled={busy}
                            onClick={() => {
                              const reason = window.prompt("取り消す理由を入力してください（記録に残ります）");
                              if (reason && reason.trim()) cancelPunch(p, reason.trim());
                            }}>取り消す</button>
                        </div>
                      ))}

                      {d.cancelled.map(p => (
                        <div key={p.id} style={{ ...st.punchRow, ...st.cancelledRow }}>
                          <span style={st.type}>{p.type}</span>
                          <span style={st.time}>{hm(p.at)}</span>
                          <span style={st.tagCancel}>取り消し済み{p.cancelReason ? `：${p.cancelReason}` : ""}</span>
                          <span style={{ flex: 1 }} />
                          <button style={st.miniBtn} disabled={busy} onClick={() => restorePunch(p)}>元に戻す</button>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </>
            )}

      {editing && (
        <EditModal punch={editing.punch} busy={busy}
          onCancel={() => setEditing(null)}
          onSave={(at, reason) => saveEdit(editing.punch, at, reason)} />
      )}
      {adding && (
        <AddModal date={adding.date} busy={busy}
          onCancel={() => setAdding(null)}
          onSave={(type, at, reason) => addPunch(type, at, reason)} />
      )}
    </div>
  );
}

function EditModal({ punch, busy, onCancel, onSave }) {
  const [value, setValue]   = useState(toLocalInput(punch.at));
  const [reason, setReason] = useState("");
  const ok = value && reason.trim();
  return (
    <div style={st.overlay}>
      <div style={st.modal}>
        <h3 style={st.modalTitle}>打刻の時刻を直す</h3>
        <p style={st.modalLead}>{punch.type}　現在：{punch.at.toLocaleString("ja-JP")}</p>
        <label style={st.label}>新しい日時</label>
        <input style={st.input} type="datetime-local" value={value} onChange={e => setValue(e.target.value)} />
        <label style={st.label}>理由（必須）</label>
        <textarea style={st.textarea} value={reason} onChange={e => setReason(e.target.value)}
          placeholder="例：本人からの申告により退勤時刻を訂正" />
        <div style={st.modalBtns}>
          <button style={st.saveBtn} disabled={!ok || busy}
            onClick={() => onSave(new Date(value), reason.trim())}>保存</button>
          <button style={st.cancelBtn} onClick={onCancel} disabled={busy}>キャンセル</button>
        </div>
      </div>
    </div>
  );
}

function AddModal({ date, busy, onCancel, onSave }) {
  const [type, setType]     = useState("出勤");
  const [value, setValue]   = useState(`${date}T09:00`);
  const [reason, setReason] = useState("");
  const ok = value && reason.trim();
  return (
    <div style={st.overlay}>
      <div style={st.modal}>
        <h3 style={st.modalTitle}>打刻を追加する</h3>
        <p style={st.modalLead}>押し忘れた打刻を、管理者として補います。</p>
        <label style={st.label}>種別</label>
        <div style={st.chipRow}>
          {["出勤", "退勤"].map(t => (
            <button key={t} type="button" style={{ ...st.chip, ...(type === t ? st.chipOn : {}) }}
              onClick={() => setType(t)}>{t}</button>
          ))}
        </div>
        <label style={st.label}>日時</label>
        <input style={st.input} type="datetime-local" value={value} onChange={e => setValue(e.target.value)} />
        <label style={st.label}>理由（必須）</label>
        <textarea style={st.textarea} value={reason} onChange={e => setReason(e.target.value)}
          placeholder="例：退勤の押し忘れ。本人申告により18:00で登録" />
        <div style={st.modalBtns}>
          <button style={st.saveBtn} disabled={!ok || busy}
            onClick={() => onSave(type, new Date(value), reason.trim())}>追加</button>
          <button style={st.cancelBtn} onClick={onCancel} disabled={busy}>キャンセル</button>
        </div>
      </div>
    </div>
  );
}

const st = {
  lead: { color: "#7f8c8d", fontSize: "13px", lineHeight: "1.8", marginTop: 0, marginBottom: "16px" },
  picker: { display: "flex", gap: "14px", alignItems: "flex-end", flexWrap: "wrap", backgroundColor: "#fff", padding: "16px 20px", borderRadius: "12px", boxShadow: "0 2px 12px rgba(0,0,0,0.08)", marginBottom: "14px" },
  label: { display: "block", fontSize: "13px", color: "#2c3e50", fontWeight: "bold", marginBottom: "6px" },
  select: { padding: "9px 12px", border: "1px solid #ddd", borderRadius: "8px", fontSize: "14px", minWidth: "220px", backgroundColor: "#fff" },
  month: { padding: "9px 12px", border: "1px solid #ddd", borderRadius: "8px", fontSize: "14px" },
  addBtn: { padding: "9px 18px", backgroundColor: "#2c3e50", color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "14px" },
  warn: { backgroundColor: "#fff6e5", border: "1px solid #f5c26b", borderRadius: "10px", padding: "12px 16px", marginBottom: "14px", fontSize: "13px", color: "#8a5a00" },
  error: { color: "#e74c3c", fontSize: "14px", marginBottom: "10px" },
  ok: { color: "#27ae60", fontSize: "14px", marginBottom: "10px" },
  empty: { color: "#999", textAlign: "center", marginTop: "32px" },
  dayCard: { backgroundColor: "#fff", borderRadius: "12px", boxShadow: "0 2px 12px rgba(0,0,0,0.08)", padding: "14px 18px", marginBottom: "10px" },
  dayBad: { borderLeft: "4px solid #e67e22" },
  dayHead: { display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap", marginBottom: "8px", color: "#2c3e50", fontSize: "14px" },
  issue: { color: "#e67e22", fontSize: "12px", fontWeight: "bold" },
  smallAdd: { marginLeft: "auto", padding: "4px 12px", backgroundColor: "#fff", color: "#2c3e50", border: "1px solid #ccd", borderRadius: "6px", cursor: "pointer", fontSize: "12px" },
  punchRow: { display: "flex", alignItems: "center", gap: "10px", padding: "7px 0", borderTop: "1px solid #f0f0f0", flexWrap: "wrap" },
  cancelledRow: { opacity: 0.6 },
  type: { fontWeight: "bold", fontSize: "13px", minWidth: "38px" },
  time: { fontSize: "15px", color: "#2c3e50", fontFamily: "monospace", minWidth: "52px" },
  tag: { fontSize: "11px", color: "#e67e22", backgroundColor: "#fff6e5", borderRadius: "10px", padding: "2px 9px" },
  tagPlain: { fontSize: "11px", color: "#7f8c8d", backgroundColor: "#f0f4f8", borderRadius: "10px", padding: "2px 9px" },
  tagCancel: { fontSize: "11px", color: "#7f8c8d", backgroundColor: "#f0f0f0", borderRadius: "10px", padding: "2px 9px" },
  miniBtn: { padding: "4px 12px", backgroundColor: "#fff", color: "#2980b9", border: "1px solid #2980b9", borderRadius: "6px", cursor: "pointer", fontSize: "12px" },
  miniDel: { padding: "4px 12px", backgroundColor: "#fff", color: "#e74c3c", border: "1px solid #e74c3c", borderRadius: "6px", cursor: "pointer", fontSize: "12px" },
  overlay: { position: "fixed", top: 0, left: 0, width: "100%", height: "100%", backgroundColor: "rgba(0,0,0,0.5)", display: "flex", justifyContent: "center", alignItems: "flex-start", zIndex: 1000, overflowY: "auto", padding: "40px 0" },
  modal: { backgroundColor: "#fff", padding: "28px 32px", borderRadius: "16px", width: "460px", maxWidth: "92vw" },
  modalTitle: { color: "#2c3e50", margin: "0 0 8px", fontSize: "18px" },
  modalLead: { color: "#7f8c8d", fontSize: "13px", marginTop: 0, marginBottom: "16px" },
  input: { width: "100%", padding: "10px 12px", marginBottom: "14px", border: "1px solid #ddd", borderRadius: "8px", fontSize: "14px", boxSizing: "border-box" },
  textarea: { width: "100%", height: "80px", padding: "10px 12px", marginBottom: "6px", border: "1px solid #ddd", borderRadius: "8px", fontSize: "14px", boxSizing: "border-box", resize: "vertical" },
  chipRow: { display: "flex", gap: "8px", marginBottom: "14px" },
  chip: { padding: "8px 18px", border: "2px solid #ddd", borderRadius: "8px", backgroundColor: "#fff", cursor: "pointer", fontSize: "14px", color: "#7f8c8d" },
  chipOn: { border: "2px solid #2c3e50", backgroundColor: "#2c3e50", color: "#fff", fontWeight: "bold" },
  modalBtns: { display: "flex", gap: "12px", marginTop: "18px" },
  saveBtn: { padding: "10px 28px", backgroundColor: "#27ae60", color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "15px" },
  cancelBtn: { padding: "10px 24px", backgroundColor: "#95a5a6", color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "15px" },
};

export default AttendanceEdit;
